<template>
    <div>
        <div id="header" class="header--notebook">
            <h2 id="top-title">
                <span class="is-saved" v-show="!isSaved">*</span>
                <edit-in-place-field @update="handleUpdate($event, 'title')" :document="note.title" :keybindings="eipKeybindings" :on-save="saveNote" ref="eipFieldRef"></edit-in-place-field>
            </h2>

            <div class="header__inner">
                <div class="header__dropdowns">
                    <div class="header__dropdowns-item">
                        <revisions :revisions="revisions" :current="note" v-if="revisions.length" class="revisions-alignright revisions-project-note"></revisions>
                    </div>
                </div>

                <a href="/project/{{ note.project_token }}" class=" header__back-btn link-back-to">Back to project</a>
            </div>

            <div class="header__mobile-actions">
                <button class="header__filters-btn button button-link js-toggle-filters">
                    <i class="icon icon-filter"></i>

                    Filters
                </button>
            </div>
        </div>

        <div id="main">
            <div class="page page-project-notebook">
                <toast :message="toastMessage" :timeout="toastTimeout" transition="fade" ref="toastRef"></toast>

                <div class="project-notebook-bar">
                    <div class="project-notebook-actions">
                        <a href="#" @click.prevent="saveNote" title="Save - [Ctrl/Cmd+S] / [Ctrl/Cmd+Enter]">Save</a>
                        <a href="#" v-show="isEditing" @click.prevent="showPreview" title="Preview - [Ctrl/Cmd+P]">Preview</a>
                        <a href="#" v-show="!isEditing" @click.prevent="showEditor" title="Edit - [Ctrl/Cmd+E]">Edit</a>
                        <a :href="`/project/${ note.project_token }/notes/create`" title="New - [Alt+Shift+N]">New</a>
                    </div>

                    <div class="project-notebook-shortcode">
                        <input type="text" readonly :value="note.shortcode" @focus="selectText($event)" />
                    </div>

                    <div class="project-notebook-shortcode">
                        <input type="text" readonly :value="note.preview_permalink" @focus="selectText($event)" />
                    </div>

                    <div class="project-notebook-syntax-hint">
                        <small>syntax: <a href="https://github.com/adam-p/markdown-here/wiki/Markdown-Cheatsheet" target="_blank">markdown</a></small>
                    </div>
                </div>

                <div class="project-notebook-body">
                    <div class="project-notebook-editor" v-show="isEditing && !hasErrors" ref="ace-editor-container">
                        <ace-editor @keydown="handleKeyDown" @update="handleUpdate($event, 'body')" :document.sync="note.body" :on-save="saveNote" :commands="editorCommands" ref="editorRef"></ace-editor>
                    </div>

                    <div class="project-notebook-preview" v-show="!isEditing && !hasErrors" ref="preview-container">
                        <div class="comment-entry" v-html="handleBody"></div>
                    </div>

                    <div class="project-notebook-errors" v-show="hasErrors">
                        <div class="project-notebook-errors-message">
                            <strong>Error</strong>
                            <br>
                            {{ errorMessage }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { nextTick, onMounted, ref, watch } from 'vue';
import AceEditor from '../components/AceEditor.vue'
import Toast from '../components/Toast.vue'
import Revisions from '../components/Revisions.vue'
import EditInPlaceField from '../components/EditInPlaceField.vue'
// import marked from '../lib/marked.min.js';

const $win = $(window);

export default {
    name: 'project-note',

    components: {
        AceEditor,
        Toast,
        Revisions,
        EditInPlaceField,
    },

    computed: {
        handleBody() {
            return marked.parse(this.note.body);
        }
    },

    setup() {
        const editorRef = ref(null);
        const hasErrors = ref(null);
        const isEditing = ref(true);
        const toastRef = ref(null);
        const eipFieldRef = ref(null);
        const markedRef = ref(null)

        const note = ref(window.note);
        const revisions = ref(window.revisions);
        const isSaved = ref(true);
        const errorMessage = ref('');
        const toastMessage = ref('');
        const toastTimeout = ref(1000);
        const editorCommands = ref([])
        const eipKeybindings = ref({})

        // Show the preview of entered markdown
        const showPreview = () => isEditing.value = false;

        const showEditor = () => {
            // Show the editor
            isEditing.value = true;

            // Reset the errors
            hasErrors.value = false;
            errorMessage.value = '';
        };

        const handleUpdate = (body, target) => note.value[target] = body;

        const createNote = () => location = '/project/' + note.value.project_token + '/notes/create';

        const saveNote = () => {
            var saveUrl = '/project/' + note.value.project_token + '/notes/' + note.value.token + '/update';

            if (!validate()) {
                alert('Cannot save a note without title.');
                return;
            }

            toastMessage.value = 'Saving...';
            toastRef.value.show(false /* autoHide */);
            eipFieldRef.value.leaveEdit();

            $.post(saveUrl, note.value, null, 'json')

                .done(function(resp) {
                    var isRevision = note.value.token !== resp.note.token;

                    // Update the references
                    note.value = resp.note;
                    revisions.value = resp.revisions;

                    // Update the flag
                    nextTick(function() {
                        isSaved.value = true;
                    });

                    // Show a new toast
                    toastMessage.value = note.value.title + ' saved.';
                    toastRef.value.show();

                    // Update the browser's URL
                    if (isRevision) {
                        history.pushState(null, note.value.title, note.value.permalink);
                    }
                })

                .fail(function(resp) {
                    // Show a new toast with the error
                    toastMessage.value = 'An error occured while saving your note.'
                    toastRef.value.show();

                    // Show the error in place of preview
                    errorMessage.value = resp.responseText;
                    hasErrors.value = true;
                    isEditing.value = false;
                });
        };

        const validate = () => note.value.title.trim() !== '';
        const selectText = (e) => e.target.select();

        editorCommands.value = [
            {
                name: 'save',
                bindKey: {
                    win: 'Ctrl-S',
                    mac: 'Command-S'
                },
                exec: saveNote
            },

            {
                name: 'save-alt',
                bindKey: {
                    win: 'Ctrl-Return',
                    mac: 'Command-Return'
                },
                exec: saveNote
            },

            {
                name: 'show-preview',
                bindKey: {
                    win: 'Ctrl-P',
                    mac: 'Command-P'
                },
                exec: showPreview
            },

            {
                name: 'new-note',
                bindKey: 'Alt-Shift-N',
                exec: createNote
            }
        ];

        eipKeybindings.value = {
            'ctrl+s meta+s ctrl+return meta+return': saveNote,
            'ctrl+p meta+p': showPreview,
            'ctrl+e meta+e': showEditor,
            'alt+shift+n': createNote,
        }

        const handleKeyDown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
                event.preventDefault();
            }
        }

        // The "save" shortcuts
        Object.keys(eipKeybindings.value).forEach((keybinding) => {
            $win.bind('keydown.notebook', keybinding, function(e) {
                e.preventDefault();
                eipKeybindings.value[keybinding]();
            });
        });

        onMounted(() => {
            markedRef.value = marked;

            // Reload the page when the "Back" button is clicked
            // and there is a previous note state in the history
            $win.bind('popstate.notebook', function(e) {
                location.reload();
                e.preventDefault();
            });

            // Confirm the exit
            $win.bind('beforeunload.notebook', function(e) {
                if (!isSaved.value) {
                    return 'You have unsaved changes. Are you sure you want to proceed?';
                }
            });
        });

        watch(() => note.value.body, (newValue, oldValue) => isSaved.value = newValue === oldValue);

        return {
            toastRef,
            editorRef,
            eipFieldRef,
            markedRef,

            showPreview,
            showEditor,
            handleUpdate,
            createNote,
            saveNote,
            validate,
            selectText,
            editorCommands,
            handleKeyDown,

            eipKeybindings,
            isEditing,
            hasErrors,
            note,
            revisions,
            isSaved,
            errorMessage,
            toastMessage,
            toastTimeout,

        }
    },

    beforeDestroy: function() {
        // Clean any attached handlers
        $win.unbind('resize.notebook');
        $win.unbind('keydown.notebook');
        $win.unbind('popstate.notebook');
        $win.unbind('beforeunload.notebook');
    },
}
</script>
